'use client';

import { useCerFeatures } from './use-cer-features';

import type { DailyRecommendationsMember, DailyRecommendationsReason } from '@slims/ui/organisms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';

interface DailyRecommendationsResponse {
	available: boolean;
	date: string | null;
	horizonDays?: number;
	members: DailyRecommendationsMember[];
	totals: { todayKwh: number; monthlyKwh: number; members: number };
	reason?: DailyRecommendationsReason;
}

interface UseDailyRecommendationsReturn extends Omit<DailyRecommendationsResponse, 'reason'> {
	horizonDays: number;
	reason: DailyRecommendationsReason | null;
	isLoading: boolean;
	error: string | null;
}

const EMPTY: DailyRecommendationsResponse = {
	available: false,
	date: null,
	members: [],
	totals: { todayKwh: 0, monthlyKwh: 0, members: 0 },
};

/**
 * Cadenza di rinfresco allineata a `useForecastData`: il piano poggia sulle
 * stesse curve, e aggiornarlo con un ritmo diverso lascerebbe le due card della
 * stessa pagina a raccontare due giornate diverse.
 */
const POLL_MS = 15 * 60 * 1000;

/**
 * Raccomandazioni giornaliere per socio, in energia condivisa (INB-42).
 *
 * ADR-006: backed by React Query — `refetchInterval` di 15 min, il canale
 * `energy_readings` invalida l'entry.
 */
export function useDailyRecommendations(orgId?: string | null): UseDailyRecommendationsReturn {
	const queryClient = useQueryClient();
	const { features } = useCerFeatures(orgId);
	const enabled = !!orgId && features.aiSuggestions !== 'disabled';

	const { data, isLoading, error } = useQuery({
		queryKey: ['daily-recommendations', orgId],
		enabled,
		refetchInterval: POLL_MS,
		queryFn: () =>
			api.get<DailyRecommendationsResponse>('/api/daily-recommendations', {
				orgId: orgId as string,
			}),
	});

	useEffect(() => {
		return subscribeToTable(
			'daily-recommendations-realtime',
			{ event: '*', schema: 'public', table: 'energy_readings' },
			() => {
				queryClient.invalidateQueries({ queryKey: ['daily-recommendations'] });
			},
		);
	}, [queryClient]);

	// Una risposta senza `members` (un errore, un proxy che risponde altro) non
	// deve far cadere tutta la pagina che ospita il pannello: si mostra vuoto.
	const value = enabled && data && Array.isArray(data.members) ? data : EMPTY;

	return {
		...value,
		horizonDays: value.horizonDays ?? 0,
		reason: value.reason ?? null,
		isLoading: orgId ? isLoading : true,
		error: error ? (error instanceof Error ? error.message : 'Errore raccomandazioni') : null,
	};
}
